import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Shield, TrendingUp, DollarSign, BarChart2, Zap, Users, AlertCircle, CheckCircle } from 'lucide-react';

const FEATURES = [
  { Icon:DollarSign, c:'#00c566', title:'0.5% Royalties',    desc:'On every trade, auto-paid'         },
  { Icon:TrendingUp, c:'#f5c842', title:'Zero Capital Risk',  desc:'Platform seeds your markets'       },
  { Icon:BarChart2,  c:'#ff6b35', title:'Live Analytics',     desc:'Track volume & earnings'           },
  { Icon:Users,      c:'#00c566', title:'Audience Revenue',   desc:'Monetize your followers'           },
  { Icon:Zap,        c:'#f5c842', title:'Instant Payouts',    desc:'Bank or USDC, anytime'             },
  { Icon:Shield,     c:'#ff6b35', title:'Non-Custodial',      desc:'Smart contracts, transparent'      },
];

export default function LoginPage() {
  const { setCreator } = useAuth();
  const navigate = useNavigate();
  const tgRef = useRef(null);
  const [status, setStatus] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    window.onTelegramAuth = async (user) => {
      setStatus('Connecting...'); setErr('');
      try {
        const res = await fetch(API + '/api/creator/auth', {
          method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(user),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error||'Auth failed');
        localStorage.setItem('np_creator_token', data.token);
        setCreator(data.creator);
        navigate('/dashboard');
      } catch(e) { setErr(e.message); setStatus(''); }
    };
    if (tgRef.current && !tgRef.current.querySelector('script')) {
      const s = document.createElement('script');
      s.src='https://telegram.org/js/telegram-widget.js?22';
      s.setAttribute('data-telegram-login','NaijaPredictCreators_Bot');
      s.setAttribute('data-size','large');
      s.setAttribute('data-onauth','onTelegramAuth(user)');
      s.setAttribute('data-request-access','write');
      s.async=true;
      tgRef.current.appendChild(s);
    }
  }, []);

  return (
    <div style={{ minHeight:'100vh', background:'#07090a', fontFamily:"'Inter',system-ui,sans-serif", color:'#e8eaeb' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        body{background:#07090a;color:#e8eaeb}
        .lp-wrap{display:flex;flex-direction:column;min-height:100vh}
        .lp-left{padding:40px 20px 32px;background:linear-gradient(160deg,#0d1114,#071a0f)}
        .lp-right{flex:1;padding:32px 20px 48px}
        .feature-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:24px}
        .stats-row{display:flex;gap:28px;margin-top:24px;padding-top:20px;border-top:1px solid rgba(255,255,255,.06)}
        @media(min-width:900px){
          .lp-wrap{flex-direction:row}
          .lp-left{flex:1;padding:60px 56px;display:flex;flex-direction:column;justify-content:center}
          .lp-right{width:440px;display:flex;align-items:center;justify-content:center;padding:48px 40px;border-left:1px solid rgba(255,255,255,.06)}
        }
      `}</style>

      <div className="lp-wrap">

        {/* LEFT — Hero */}
        <div className="lp-left">
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:32 }}>
            <img src="/logo.svg" alt="NaijaPredict" style={{ width:44, height:44, borderRadius:10 }}/>
            <div>
              <div style={{ fontWeight:900, fontSize:18, letterSpacing:.5 }}>NAIJAPREDICT</div>
              <div style={{ fontSize:10, color:'#00c566', fontWeight:700, letterSpacing:2, marginTop:2 }}>CREATOR PORTAL</div>
            </div>
          </div>

          <h1 style={{ fontSize:'clamp(28px,5vw,44px)', fontWeight:900, lineHeight:1.15, marginBottom:14 }}>
            Monetize Your<br/>
            <span style={{ background:'linear-gradient(135deg,#00c566,#f5c842)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
              Predictions
            </span>
          </h1>

          <p style={{ fontSize:15, color:'#4a5a63', lineHeight:1.75, maxWidth:420, marginBottom:4 }}>
            Create prediction markets for your audience. Earn <strong style={{ color:'#00c566' }}>0.5% royalties</strong> on every trade with zero capital at risk.
          </p>

          <div className="feature-grid">
            {FEATURES.map(({ Icon, c, title, desc }) => (
              <div key={title} style={{ background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.06)', borderRadius:10, padding:12 }}>
                <div style={{ width:30, height:30, borderRadius:8, background:`rgba(${c==='#00c566'?'0,197,102':c==='#f5c842'?'245,200,66':'255,107,53'},.1)`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:8 }}>
                  <Icon size={14} color={c} strokeWidth={2}/>
                </div>
                <div style={{ fontSize:12, fontWeight:700, marginBottom:2 }}>{title}</div>
                <div style={{ fontSize:11, color:'#4a5a63' }}>{desc}</div>
              </div>
            ))}
          </div>

          <div className="stats-row">
            {[['242+','Traders'],['$0','Your Risk'],['0.5%','Royalty']].map(([v,l])=>(
              <div key={l}>
                <div style={{ fontSize:22, fontWeight:800, color:'#00c566' }}>{v}</div>
                <div style={{ fontSize:11, color:'#4a5a63', marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — Login Card */}
        <div className="lp-right">
          <div style={{ width:'100%', maxWidth:380 }}>

            {/* Beta Banner */}
            <div style={{ background:'rgba(245,200,66,.07)', border:'1px solid rgba(245,200,66,.18)', borderRadius:12, padding:'10px 14px', marginBottom:24, display:'flex', gap:10, alignItems:'flex-start' }}>
              <AlertCircle size={14} color="#f5c842" strokeWidth={2} style={{ flexShrink:0, marginTop:1 }}/>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:'#f5c842' }}>Beta — Under Active Development</div>
                <div style={{ fontSize:11, color:'#4a5a63', marginTop:2 }}>Full launch coming soon. Some features limited.</div>
              </div>
            </div>

            {/* Card */}
            <div style={{ background:'#0d1114', border:'1px solid rgba(255,255,255,.07)', borderRadius:20, padding:28 }}>
              <div style={{ textAlign:'center', marginBottom:24 }}>
                <img src="/logo.svg" alt="" style={{ width:56, height:56, borderRadius:14, marginBottom:14 }}/>
                <h2 style={{ fontSize:20, fontWeight:800, marginBottom:6 }}>Creator Sign In</h2>
                <p style={{ fontSize:13, color:'#4a5a63', lineHeight:1.6 }}>Connect your Telegram to access your dashboard</p>
              </div>

              <div style={{ display:'flex', justifyContent:'center', marginBottom:status||err?12:0 }} ref={tgRef}/>

              {status && (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, fontSize:13, color:'#00c566', marginBottom:12 }}>
                  <Zap size={13}/>{status}
                </div>
              )}

              {err && (
                <div style={{ background:'rgba(255,68,68,.07)', border:'1px solid rgba(255,68,68,.2)', borderRadius:10, padding:'10px 12px', marginBottom:12, fontSize:12, color:'#ff4444', display:'flex', alignItems:'center', gap:8 }}>
                  <AlertCircle size={13} style={{ flexShrink:0 }}/>{err}
                </div>
              )}

              <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:20, paddingTop:20, borderTop:'1px solid rgba(255,255,255,.06)' }}>
                {[[Shield,'Verified via Telegram — no password'],[Zap,'Instant dashboard access'],[CheckCircle,'Earn from your first market']].map(([Icon,t])=>(
                  <div key={t} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#4a5a63' }}>
                    <Icon size={12} color="#00c566" strokeWidth={2} style={{ flexShrink:0 }}/>{t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
